import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ChevronRight } from 'lucide-react';
import { buildDetectionQuery } from '../utils/marketOverride';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ComplianceBanner — thin jurisdiction strip shown above every checkout
 * CTA. Reads /api/compliance/me (auto-detects country from IP) and
 * surfaces VAT/GST/HST inclusive pricing + regulator reference + a link
 * to Terms of Service relevant to the detected country.
 */
const ComplianceBanner = ({ compact = false, className = '' }) => {
  const [fw, setFw] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Browser timezone + admin test override (see utils/marketOverride.js).
    const url = `${API_URL}/api/compliance/me${buildDetectionQuery()}`;
    axios.get(url)
      .then((r) => { if (!cancelled) setFw(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!fw) return null;
  const { country_code, country_name, currency, tax, legal, localised } = fw;

  if (compact) {
    return (
      <div className={`flex items-center gap-1.5 text-[10px] text-slate-500 ${className}`} data-testid="compliance-banner-compact">
        <ShieldCheck className="h-3 w-3 text-[#1F3D2B]" />
        <span>{country_name} · Fees incl. {tax.label} ({tax.rate_pct}%) · <a href={legal.terms_url} className="underline hover:text-[#E07A3A]">Terms</a></span>
      </div>
    );
  }

  return (
    <div className={`border-l-4 border-[#1F3D2B] bg-[#F8FAFC] px-4 py-3 ${className}`} data-testid="compliance-banner">
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-[#1F3D2B] flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-xs">
          <div className="flex flex-wrap gap-x-3 gap-y-1 items-baseline">
            <span className="text-[10px] tracking-widest text-[#1F3D2B] uppercase font-semibold">{country_name} compliance</span>
            <span className="text-slate-600">Fees include <strong>{tax.label} @ {tax.rate_pct}%</strong> ({currency})</span>
            <span className="text-slate-500">· {legal.regime}</span>
          </div>
          {!localised && (
            <p className="text-[11px] text-amber-600 mt-1">
              No local framework configured for your region — global defaults apply.
            </p>
          )}
          <button onClick={() => setExpanded((s) => !s)} className="inline-flex items-center gap-1 mt-1 text-[11px] text-slate-500 hover:text-[#E07A3A]" data-testid="compliance-banner-toggle">
            {expanded ? 'Hide' : 'Show'} disclaimer <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
          {expanded && (
            <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">
              {legal.disclaimer}{' '}
              <a href={legal.terms_url} className="underline hover:text-[#E07A3A]">Terms</a>
              {' · '}
              <a href={legal.privacy_url} className="underline hover:text-[#E07A3A]">Privacy</a>
              {tax.note ? ` · ${tax.note}` : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceBanner;
